import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import {
  Wind,
  Flame,
  Ban,
  Waves,
  CloudHail,
  Cloud,
  Droplets,
  Sprout,
  CheckCircle2,
  ArrowRight,
  AlertTriangle,
  Phone,
  Calculator,
} from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";
import Breadcrumbs from "./Breadcrumbs";

const SERVICE_TOPIC = {
  "wind-damage": "wind",
  "water-damage": "water",
  "denied-or-underpaid-claims": "denied",
};
import SEO from "./SEO";
import CalculatorCallout from "./CalculatorCallout";
import RelatedClaimHelp from "./RelatedClaimHelp";
import ContentSections from "./ContentSections";
import ReviewedBy from "./ReviewedBy";
import { damageServices, cities, cityUrl, damageUrl } from "../data/seoContent";
import { businessInfo } from "../mock";
import { trackCalcEntry } from "../utils/analytics";
import {
  localBusinessSchema,
  serviceSchema,
  faqSchema,
  breadcrumbSchema,
} from "../utils/schemas";

const iconMap = {
  Wind,
  Flame,
  Ban,
  Waves,
  CloudHail,
  Cloud,
  Droplets,
  Sprout,
};

const ServicePage = () => {
  const { slug } = useParams();
  const service = damageServices[slug];

  if (!service) return <Navigate to="/" replace />;

  const Icon = iconMap[service.icon] || Wind;
  const cityList = Object.values(cities);
  const otherServices = Object.values(damageServices).filter(
    (s) => s.slug !== slug
  );
  // Strip trailing " Public Adjuster" from the title so prose reads naturally
  // (e.g. "Water Damage Public Adjuster" → "Water Damage"). The full title is
  // still used for SEO schema, breadcrumbs, and meta tags.
  const damageType = service.title.replace(/\s*Public Adjuster\s*$/i, "").trim();

  const schemas = [
    localBusinessSchema(),
    serviceSchema({
      name: service.title,
      description: service.metaDescription,
      slug: service.slug,
    }),
    breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Services", path: "/#services" },
      { name: service.title, path: `/${service.slug}` },
    ]),
    ...(service.faqs ? [faqSchema(service.faqs)] : []),
  ];

  return (
    <div className="bg-black min-h-screen text-white">
      <SEO
        title={service.metaTitle}
        description={service.metaDescription}
        path={`/${service.slug}`}
        keywords={service.keywords}
        image={`https://claimbeast.com${service.image}`}
        schemas={schemas}
      />
      <Header />

      <Breadcrumbs
        items={[
          { name: "Services", path: "/#services" },
          { name: service.title, path: `/${service.slug}` },
        ]}
      />

      {/* Hero */}
      <section className="relative bg-black overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={service.image}
            alt={`${service.title} in Tampa Bay`}
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-24">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-14 w-14 rounded-sm bg-[#E11D26]/20 border border-[#E11D26]/50 flex items-center justify-center text-[#E11D26]">
                <Icon className="h-7 w-7" strokeWidth={1.8} />
              </span>
              <span className="text-[#E11D26] text-xs sm:text-sm font-bold tracking-[0.25em] uppercase">
                Tampa Bay · Riverview, FL
              </span>
            </div>
            <h1
              className="text-white text-4xl sm:text-5xl md:text-6xl font-black uppercase leading-[0.95]"
              style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
            >
              {service.h1}
            </h1>
            <p className="mt-6 text-white/85 text-base sm:text-lg leading-relaxed max-w-2xl">
              {service.intro}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/#contact"
                className="group inline-flex items-center justify-between gap-6 bg-[#E11D26] hover:bg-[#c11820] text-white font-bold uppercase tracking-wider px-7 py-4 transition-colors"
              >
                <span className="text-sm underline underline-offset-4">
                  Get a Free Claim Review
                </span>
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Link>
              <a
                href={`tel:${businessInfo.phoneRaw}`}
                className="inline-flex items-center justify-center gap-2 bg-white text-black hover:bg-white/90 font-bold uppercase tracking-wider px-7 py-4 transition-colors"
              >
                <Phone className="h-4 w-4" />
                <span className="text-sm">{businessInfo.phone}</span>
              </a>
            </div>
            {service.calcPage && (
              <Link
                to={service.calcPage.href}
                data-testid="service-calc-link"
                onClick={() =>
                  trackCalcEntry(
                    "service_to_calc_click",
                    `service_${service.slug}`,
                    { calculator: service.calcPage.href.replace("/", "") }
                  )
                }
                className="mt-5 inline-flex items-center gap-2 text-[#E11D26] hover:text-white font-bold tracking-wider uppercase text-sm transition-colors"
              >
                <Calculator className="h-4 w-4" />
                {service.calcPage.label}
                <ArrowRight className="h-4 w-4" />
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Common issues + why hard */}
      <section className="py-16 md:py-20 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <h2
              className="text-3xl sm:text-4xl font-black text-white uppercase leading-tight"
              style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
            >
              Common <span className="text-[#E11D26]">{service.title.split(" ")[0]}</span> Claim Issues We Handle
            </h2>
            <ul className="mt-7 grid sm:grid-cols-2 gap-3">
              {service.commonIssues.map((issue, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 bg-[#0F0F0F] border border-white/10 p-4"
                >
                  <CheckCircle2 className="h-5 w-5 text-[#E11D26] shrink-0 mt-0.5" />
                  <span className="text-white/85 text-sm leading-relaxed">
                    {issue}
                  </span>
                </li>
              ))}
            </ul>
          </div>
          <aside className="lg:col-span-5">
            <div className="bg-gradient-to-br from-[#1a0709] to-[#0F0F0F] border-l-4 border-[#E11D26] p-7">
              <div className="flex items-center gap-2 text-[#E11D26] text-xs font-bold tracking-[0.25em] uppercase">
                <AlertTriangle className="h-4 w-4" />
                Why This Claim Is Hard
              </div>
              <p className="mt-4 text-white/85 leading-relaxed">
                {service.whyHard}
              </p>
              <Link
                to="/#contact"
                className="mt-6 inline-flex items-center gap-2 text-white font-bold tracking-wider uppercase text-sm hover:text-[#E11D26] transition-colors"
              >
                Talk to a public adjuster
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </aside>
        </div>
      </section>

      {service.extraSections && (
        <ContentSections sections={service.extraSections} />
      )}

      {/* Calculator callout — service-specific */}
      <CalculatorCallout
        eyebrow={`${damageType} · 60-Second Estimate`}
        headline={`Know what your ${damageType.toLowerCase()} claim should be worth.`}
        body={`${damageType} cases get underpaid more than almost any other claim type in Florida — carriers lean on depreciation, missing line items, and outdated unit pricing to shrink the number. Run your damage through our calculator and you'll see a low–high range built from current Florida adjuster pricing for ${damageType.toLowerCase()} losses, so you can sanity-check whatever the insurance company puts on paper.`}
        ctaLabel={`Estimate My ${damageType.split(" ")[0]} Claim`}
      />

      {/* Cities served */}
      <section className="py-16 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2
            className="text-3xl sm:text-4xl font-black text-white uppercase leading-tight"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            {service.title.split(" ")[0]} Claim Help In Every{" "}
            <span className="text-[#E11D26]">Tampa Bay City</span>
          </h2>
          <p className="mt-3 text-white/65 max-w-2xl">
            Based in Riverview, we handle {damageType.toLowerCase()} claims
            across all of these cities and their surrounding communities.
          </p>
          <div className="mt-7 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {cityList.map((city) => (
              <Link
                key={city.slug}
                to={cityUrl(city.slug)}
                className="bg-[#0F0F0F] hover:bg-[#E11D26] hover:text-white transition-colors p-4 text-white/85 text-sm font-bold tracking-wide uppercase"
              >
                {city.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Service-specific FAQ (renders + feeds FAQPage schema) */}
      {service.faqs && (
        <section className="py-16 md:py-20 bg-black border-t border-white/10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="text-[#E11D26] text-xs font-bold tracking-[0.3em] uppercase mb-3">
              {service.title.split(" ")[0]} Claim FAQs · Florida
            </div>
            <h2
              className="text-3xl sm:text-4xl font-black text-white uppercase leading-tight"
              style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
            >
              {damageType}{" "}
              <span className="text-[#E11D26]">Questions Answered</span>
            </h2>
            <div className="mt-9 divide-y divide-white/10 border-y border-white/10">
              {service.faqs.map((f, i) => (
                <details
                  key={i}
                  className="group py-5"
                  data-testid={`service-faq-${i}`}
                >
                  <summary className="flex justify-between items-start cursor-pointer list-none gap-6">
                    <h3 className="text-white text-base sm:text-lg font-bold uppercase tracking-wide leading-snug">
                      {f.question}
                    </h3>
                    <span className="text-[#E11D26] text-xl group-open:rotate-45 transition-transform shrink-0">
                      +
                    </span>
                  </summary>
                  <p className="mt-4 text-white/75 leading-relaxed text-sm sm:text-base">
                    {f.answer}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Other services */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2
            className="text-3xl sm:text-4xl font-black text-white uppercase leading-tight"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            Other <span className="text-[#E11D26]">Damage Types</span> We Handle
          </h2>
          <div className="mt-7 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {otherServices.map((s) => {
              const SIcon = iconMap[s.icon];
              return (
                <Link
                  key={s.slug}
                  to={damageUrl(s.slug)}
                  className="group bg-[#0F0F0F] hover:bg-[#141414] p-6 transition-colors"
                >
                  <div className="h-12 w-12 rounded-sm flex items-center justify-center bg-[#E11D26]/10 border border-[#E11D26]/30 text-[#E11D26] mb-4 group-hover:bg-[#E11D26] group-hover:text-white transition-colors">
                    <SIcon className="h-6 w-6" strokeWidth={1.8} />
                  </div>
                  <div
                    className="text-white font-black uppercase text-lg tracking-wide"
                    style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
                  >
                    {s.title}
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <ReviewedBy />

      <RelatedClaimHelp
        topic={SERVICE_TOPIC[service.slug] || "generic"}
        currentPath={`/${service.slug}`}
      />

      {["wind-damage", "water-damage", "flood-damage"].includes(service.slug) && (
        <section className="py-10 bg-black border-t border-white/10" data-testid="service-nwfl-link">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <p className="text-white/70 text-sm sm:text-base">
              Property in Northwest Florida? See our Hurricane Isaias claim help for{" "}
              <Link to="/hurricane-isaias-pensacola" className="text-[#E11D26] hover:text-white underline underline-offset-2 font-semibold">
                Pensacola
              </Link>
              {" and "}
              <Link to="/hurricane-isaias-destin" className="text-[#E11D26] hover:text-white underline underline-offset-2 font-semibold">
                Destin &amp; the Emerald Coast
              </Link>
              .
            </p>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="py-14 bg-[#E11D26]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <h3
            className="text-white text-2xl sm:text-3xl md:text-4xl font-black uppercase leading-tight"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            Ready to fight back? Get a free claim review.
          </h3>
          <Link
            to="/#contact"
            className="group inline-flex items-center gap-3 bg-black hover:bg-[#0A0A0A] text-white font-bold uppercase tracking-wider px-7 py-4 transition-colors"
          >
            <span className="text-sm">Start Free Review</span>
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicePage;
